// src/components/superadmin/CampusDashboard/StaffManagement.jsx
import React, { useState, useEffect } from "react";
import StaffList from "./StaffList";
import StaffFormModal from "./StaffFormModal";
import StaffViewModal from "./StaffViewModal";
import { MdPersonAdd } from "react-icons/md";
import { API_BASE_URL, getAuthHeaders } from "../../../utils/api";

const StaffManagement = ({ 
  campusId, 
  campusName, 
  showToast, 
  onBack,
  onLogout 
}) => {
  const [staff, setStaff] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [viewingStaff, setViewingStaff] = useState(null);
  const [editingStaff, setEditingStaff] = useState(null);
  const [loading, setLoading] = useState(true);

  // Load staff data
  const loadStaff = async () => {
    if (!campusId) return;
    
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/campuses/${campusId}/staff`, {
        headers: getAuthHeaders(),
      });
      
      if (res.status === 401) {
        onLogout?.();
        return;
      }
      
      if (!res.ok) throw new Error("Failed to load staff");
      
      const data = await res.json();
      setStaff(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Failed to load staff:", error);
      showToast?.("error", "Load Error", "Could not load staff members.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadStaff();
  }, [campusId]);

  // Save staff (create or update)
  const handleSaveStaff = async (staffData) => {
    try {
      const isEditing = !!editingStaff;
      const url = isEditing 
        ? `${API_BASE_URL}/api/campuses/${campusId}/staff/${editingStaff.id}`
        : `${API_BASE_URL}/api/campuses/${campusId}/staff`;
      
      const method = isEditing ? "PUT" : "POST";
      
      // Remove id from data for POST, keep for PUT
      const { id, ...dataToSend } = staffData;
      const payload = isEditing ? staffData : dataToSend;
      
      const res = await fetch(url, {
        method,
        headers: {
          ...getAuthHeaders(),
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });
      
      if (res.status === 401) {
        onLogout?.();
        return false;
      }
      
      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.detail || "Failed to save staff");
      }
      
      const savedStaff = await res.json();
      
      showToast?.(
        "success",
        isEditing ? "Staff Updated" : "Staff Added Successfully",
        `${staffData.name} has been ${isEditing ? "updated" : "added"} successfully.`
      );
      
      setIsModalOpen(false);
      setEditingStaff(null);
      await loadStaff(); // Refresh the list
      return true;
    } catch (error) {
      console.error("Failed to save staff:", error);
      showToast?.("error", "Save Failed", error.message || "Could not save staff member.");
      return false;
    }
  };

  // Delete staff
  const handleDeleteStaff = async (staffId) => {
    if (!window.confirm("Are you sure you want to delete this staff member?")) {
      return;
    }
    
    try {
      const res = await fetch(`${API_BASE_URL}/api/campuses/${campusId}/staff/${staffId}`, {
        method: "DELETE",
        headers: getAuthHeaders(),
      });
      
      if (res.status === 401) {
        onLogout?.();
        return;
      }
      
      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.detail || "Failed to delete staff");
      }
      
      showToast?.("success", "Staff Deleted", "Staff member has been removed successfully.");
      await loadStaff(); // Refresh the list
    } catch (error) {
      console.error("Failed to delete staff:", error);
      showToast?.("error", "Delete Failed", error.message || "Could not delete staff member.");
    }
  };

  // Handle edit
  const handleEdit = (staffMember) => {
    setEditingStaff(staffMember);
    setIsModalOpen(true);
  };

  // Handle view - opens view modal
  const handleView = (staffMember) => {
    setViewingStaff(staffMember);
    setIsViewModalOpen(true);
  };

  return (
    <div className="staff-management" style={{ padding: "20px" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
        <div>
          <h2 style={{ margin: 0, color: "#1a202c" }}>
            Staff Management
            {campusName && <span style={{ fontSize: "16px", fontWeight: 400, color: "#64748b", marginLeft: "12px" }}>
              — {campusName}
            </span>}
          </h2>
          <p style={{ margin: "4px 0 0 0", fontSize: "13px", color: "#64748b" }}>
            Total Staff: {staff.length} members
          </p>
        </div>
        <div style={{ display: "flex", gap: "12px" }}>
          {onBack && (
            <button 
              className="btn-secondary"
              onClick={onBack}
              style={{ 
                padding: "8px 16px",
                borderRadius: "8px",
                border: "1px solid #e2e8f0",
                background: "#fff",
                cursor: "pointer"
              }}
            >
              Back
            </button>
          )}
          <button 
            className="btn-primary"
            onClick={() => {
              setEditingStaff(null);
              setIsModalOpen(true);
            }}
            style={{ 
              display: "flex",
              alignItems: "center",
              gap: "8px",
              padding: "8px 16px",
              borderRadius: "8px",
              border: "none",
              background: "#0f1b2d",
              color: "#fff",
              cursor: "pointer"
            }}
          >
            <MdPersonAdd size={18} />
            Add Staff
          </button>
        </div>
      </div>

      {loading ? (
        <div style={{ textAlign: "center", padding: "40px", color: "#64748b" }}>
          Loading staff members...
        </div>
      ) : (
        <StaffList
          staff={staff}
          campus={{ id: campusId, name: campusName }}
          onEdit={handleEdit}
          onDelete={handleDeleteStaff}
          onView={handleView}
        />
      )}

      <StaffFormModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setEditingStaff(null);
        }}
        onSave={handleSaveStaff}
        campus={{ id: campusId, name: campusName }}
        editingStaff={editingStaff}
      />

      <StaffViewModal
        isOpen={isViewModalOpen}
        onClose={() => {
          setIsViewModalOpen(false);
          setViewingStaff(null);
        }}
        staffMember={viewingStaff}
        campus={{ id: campusId, name: campusName }}
      />
    </div>
  );
};

export default StaffManagement;